import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  listDomains, createDomain, updateDomain, deleteDomain,
  uploadDomainLogo, uploadDomainFavicon, deleteDomainLogo, deleteDomainFavicon,
  mailDomainLogoUrl, toggleOfficeRegistration,
  MailDomain, MailDomainUpdate,
} from '../services/mailService';
import { getServerConfig } from '../services/mailService';
import { getSystemCodes, SystemCode } from '../services/systemCodeService';
import { getI18nValue } from '../utils/i18nHelper';
import { usePermission } from '../hooks/usePermission';
import { useMessage } from '../contexts/MessageContext';

import { validateSession } from '../utils/sessionValidator';
import FunctionPageHeader from '../components/FunctionPageHeader';

const FUNC = 'ee_mail_domains';

const EEMailDomainsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showApiError, showError } = useMessage();

  const hasInit = useRef(false);

  const [domains, setDomains] = useState<MailDomain[]>([]);
  const [domainOptions, setDomainOptions] = useState<SystemCode[]>([]);
  const [serverConfigId, setServerConfigId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [editTarget, setEditTarget] = useState<MailDomain | null>(null);
  const [editForm, setEditForm] = useState<MailDomainUpdate>({});
  const [saving, setSaving] = useState(false);
  const [newDomainCode, setNewDomainCode] = useState('');
  const [addingNew, setAddingNew] = useState(false);
  const [officeToggling, setOfficeToggling] = useState<string | null>(null);   // 3.2f 正在切換的網域

  const canCreate = hasPermission(FUNC, 'create');
  const canUpdate = hasPermission(FUNC, 'update');
  const canDelete = hasPermission(FUNC, 'delete');

  // #21 webmail branding 上傳（logo/favicon）
  const handleUploadAsset = async (domainCode: string, kind: 'logo' | 'favicon', file: File | undefined) => {
    if (!file) return;
    try {
      const fn = kind === 'logo' ? uploadDomainLogo : uploadDomainFavicon;
      const updated = await fn(domainCode, file);
      setDomains(prev => prev.map(d => d.domain_code === domainCode ? updated : d));
      showSuccess('SYS020001', { name: `${domainCode} ${kind}` });
    } catch (err) { showApiError(err); }
  };

  // 3.2f · toggle Office 支援（主網域 register 一次 · subdomain wildcard zero-touch · 觸發 Collabora config 重生）
  const handleOfficeToggle = async (domainCode: string, enabled: boolean) => {
    if (enabled && !window.confirm(
      t('mail.domains.officeToggleConfirm',
        '啟用後，此主網域及其所有 subdomain 將自動支援線上 Office 編輯（Collabora alias）。是否確認？'))) return;
    setOfficeToggling(domainCode);
    try {
      const updated = await toggleOfficeRegistration(domainCode, enabled);
      setDomains(prev => prev.map(d => d.domain_code === domainCode ? updated : d));
      showSuccess('SYS020001', { name: `${domainCode} Office 支援` });
    } catch (err) {
      showApiError(err);
    } finally {
      setOfficeToggling(null);
    }
  };

  // #21 移除 webmail branding（二次確認）
  const handleDeleteAsset = async (domainCode: string, kind: 'logo' | 'favicon') => {
    if (!window.confirm(`${domainCode} ${kind} — ${t('common.confirmDelete', '確定要刪除嗎？')}`)) return;
    try {
      const fn = kind === 'logo' ? deleteDomainLogo : deleteDomainFavicon;
      const updated = await fn(domainCode);
      setDomains(prev => prev.map(d => d.domain_code === domainCode ? updated : d));
      showSuccess('SYS020001', { name: `${domainCode} ${kind}` });
    } catch (err) { showApiError(err); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [ds, cfg, opts] = await Promise.all([
        listDomains(),
        getServerConfig(),
        getSystemCodes({ code_type: 'Domain_name', is_active: true }),
      ]);
      setDomains(ds);
      setServerConfigId(cfg.id);
      setDomainOptions(opts);
    } catch (err) { showApiError(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!permLoading && !hasInit.current) {
      hasInit.current = true;
      validateSession(FUNC).then(v => { if (v) load(); });
    }
  }, [permLoading]);

  const startEdit = (d: MailDomain) => {
    setEditTarget(d);
    setEditForm({
      server_config_id: d.server_config_id ?? null,
      default_quota: d.default_quota ?? '',
      tenant_enabled: d.tenant_enabled,
      admin_only: d.admin_only,
    });
  };

  const handleSave = async () => {
    if (!editTarget) return;
    setSaving(true);
    try {
      await updateDomain(editTarget.domain_code, editForm);
      showSuccess('SYS020001', { name: editTarget.domain_code });
      setEditTarget(null);
      await load();
    } catch (err) { showApiError(err); }
    finally { setSaving(false); }
  };

  const handleCreate = async () => {
    if (!newDomainCode.trim()) {
      showError('ERR020006', { field: 'domain_code', detail: t('common.required') });
      return;
    }
    setSaving(true);
    try {
      await createDomain(newDomainCode.trim(), {
        server_config_id: serverConfigId,
        tenant_enabled: false,
        admin_only: false,
      });
      showSuccess('SYS020001', { name: newDomainCode });
      setNewDomainCode('');
      setAddingNew(false);
      await load();
    } catch (err) { showApiError(err); }
    finally { setSaving(false); }
  };

  const handleDelete = async (d: MailDomain) => {
    if (!window.confirm(t('common.confirmDelete', { name: d.domain_code }))) return;
    try {
      await deleteDomain(d.domain_code);
      showSuccess('SYS020001', { name: d.domain_code });
      await load();
    } catch (err) { showApiError(err); }
  };

  const thStyle: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', background: '#f5f5f5', borderBottom: '1px solid #ddd' };
  const tdStyle: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid #eee' };

  return (
    <div>
      <FunctionPageHeader funcCode={FUNC} />
      <div style={{ padding: '12px 24px' }}>
        {canCreate && (
          <div style={{ marginBottom: 16 }}>
            {addingNew ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select
                  value={newDomainCode}
                  onChange={e => setNewDomainCode(e.target.value)}
                  style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: 4, width: 260 }}
                >
                  <option value="">{t('mail.domains.domainCodePlaceholder')}</option>
                  {domainOptions
                    .filter(opt => !domains.some(d => d.domain_code === opt.code))
                    .map(opt => (
                      <option key={opt.code} value={opt.code}>
                        {opt.code}{getI18nValue(opt.code_name, i18n.language) !== opt.code
                          ? ` — ${getI18nValue(opt.code_name, i18n.language)}`
                          : ''}
                      </option>
                    ))
                  }
                </select>
                <button onClick={handleCreate} disabled={saving}
                  style={{ padding: '6px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                  {t('common.confirm')}
                </button>
                <button onClick={() => { setAddingNew(false); setNewDomainCode(''); }}
                  style={{ padding: '6px 16px', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>
                  {t('common.cancel')}
                </button>
              </div>
            ) : (
              <button onClick={() => setAddingNew(true)}
                style={{ padding: '6px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                + {t('mail.domains.addDomain')}
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div>{t('common.loading')}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr>
                <th style={thStyle}>{t('mail.domains.domainCode')}</th>
                <th style={thStyle}>{t('mail.domains.container')}</th>
                <th style={thStyle}>{t('mail.domains.defaultQuota')}</th>
                <th style={thStyle}>{t('mail.domains.tenantEnabled')}</th>
                <th style={thStyle}>{t('mail.domains.adminOnly')}</th>
                <th style={thStyle}>{t('mail.domains.configured')}</th>
                <th style={thStyle}>{t('mail.domains.officeSupport', 'Office 支援')}</th>
                <th style={thStyle}>{t('mail.domains.usedByTenants', '使用租戶')}</th>
                <th style={thStyle}>
                  {t('mail.domains.webmailBranding', 'Webmail 品牌')}
                  <div style={{ fontSize: 11, color: '#666', fontWeight: 'normal', marginTop: 2 }}>
                    {t('mail.domains.logoHint', '💡 Logo 建議 240×60 px · Favicon 建議 32×32 px · 格式 PNG · 檔案 < 500KB')}
                  </div>
                </th>
                <th style={thStyle}>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {domains.map(d => (
                <tr key={d.domain_code}>
                  {editTarget?.domain_code === d.domain_code ? (
                    <>
                      <td style={tdStyle}><strong>{d.domain_code}</strong></td>
                      <td style={tdStyle}>{d.mail_api_url ?? '-'}</td>
                      <td style={tdStyle}>
                        <input
                          value={editForm.default_quota ?? ''}
                          onChange={e => setEditForm(f => ({ ...f, default_quota: e.target.value }))}
                          placeholder="e.g. 500M"
                          style={{ padding: '4px 8px', width: 90, border: '1px solid #ccc', borderRadius: 4 }}
                        />
                      </td>
                      <td style={tdStyle}>
                        <input type="checkbox"
                          checked={editForm.tenant_enabled ?? false}
                          onChange={e => setEditForm(f => ({ ...f, tenant_enabled: e.target.checked }))} />
                      </td>
                      <td style={tdStyle}>
                        <input type="checkbox"
                          checked={editForm.admin_only ?? false}
                          onChange={e => setEditForm(f => ({ ...f, admin_only: e.target.checked }))} />
                      </td>
                      <td style={tdStyle}>{d.is_configured ? '✓' : '✗'}</td>
                      <td style={tdStyle}>{d.office_registered ? '✓' : '✗'}</td>
                      <td style={tdStyle}>
                        {(d.used_by_tenants ?? []).length === 0
                          ? <span style={{ color: '#999' }}>—</span>
                          : (d.used_by_tenants ?? []).map(tt => (
                              <span key={tt.org_id} style={{ display: 'inline-block', padding: '2px 8px', margin: 2, background: '#eef', borderRadius: 10, fontSize: 12 }}>
                                {tt.org_name}
                              </span>
                            ))}
                      </td>
                      <td style={tdStyle}>
                        {d.logo_file_id
                          ? <img src={mailDomainLogoUrl(d.domain_code, d.logo_file_id)} alt="logo" style={{ height: 24, maxWidth: 80, objectFit: 'contain' }} />
                          : <span style={{ color: '#999', fontSize: 12 }}>—</span>}
                      </td>
                      <td style={tdStyle}>
                        <button onClick={handleSave} disabled={saving}
                          style={{ marginRight: 6, padding: '4px 10px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                          {t('common.save')}
                        </button>
                        <button onClick={() => setEditTarget(null)}
                          style={{ padding: '4px 10px', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>
                          {t('common.cancel')}
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={tdStyle}><strong>{d.domain_code}</strong></td>
                      <td style={tdStyle}>{d.mail_api_url ?? '-'}</td>
                      <td style={tdStyle}>{d.default_quota ?? '-'}</td>
                      <td style={tdStyle}>{d.tenant_enabled ? '✓' : '✗'}</td>
                      <td style={tdStyle}>{d.admin_only ? '✓' : '✗'}</td>
                      <td style={{ ...tdStyle, color: d.is_configured ? '#388e3c' : '#d32f2f' }}>
                        {d.is_configured ? t('common.yes') : t('common.no')}
                      </td>
                      <td style={tdStyle}>
                        <input type="checkbox"
                          checked={d.office_registered}
                          disabled={!canUpdate || officeToggling === d.domain_code}
                          onChange={e => handleOfficeToggle(d.domain_code, e.target.checked)} />
                        {d.office_registered && d.office_registered_at && (
                          <div style={{ fontSize: 11, color: '#666' }}>
                            {new Date(d.office_registered_at).toLocaleString()}
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {(d.used_by_tenants ?? []).length === 0
                          ? <span style={{ color: '#999' }}>—</span>
                          : (d.used_by_tenants ?? []).map(tt => (
                              <span key={tt.org_id} style={{ display: 'inline-block', padding: '2px 8px', margin: 2, background: '#eef', borderRadius: 10, fontSize: 12 }}>
                                {tt.org_name}
                              </span>
                            ))}
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {d.logo_file_id
                            ? <img src={mailDomainLogoUrl(d.domain_code, d.logo_file_id)} alt="logo" style={{ height: 24, maxWidth: 80, objectFit: 'contain' }} />
                            : <span style={{ color: '#999', fontSize: 12 }}>{t('mail.domains.noLogo', '無 logo')}</span>}
                          {canUpdate && (
                            <>
                              <label style={{ padding: '2px 8px', border: '1px solid #1976d2', color: '#1976d2', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                                Logo
                                <input type="file" accept="image/*" hidden
                                  onChange={e => { handleUploadAsset(d.domain_code, 'logo', e.target.files?.[0]); e.target.value = ''; }} />
                              </label>
                              {d.logo_file_id && (
                                <button type="button" onClick={() => handleDeleteAsset(d.domain_code, 'logo')}
                                  title={t('mail.domains.deleteLogo', '刪除 logo')}
                                  style={{ padding: '2px 6px', border: '1px solid #d32f2f', color: '#d32f2f', background: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>✕</button>
                              )}
                              <label style={{ padding: '2px 8px', border: '1px solid #1976d2', color: '#1976d2', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                                Favicon
                                <input type="file" accept="image/*" hidden
                                  onChange={e => { handleUploadAsset(d.domain_code, 'favicon', e.target.files?.[0]); e.target.value = ''; }} />
                              </label>
                              {d.favicon_file_id && (
                                <button type="button" onClick={() => handleDeleteAsset(d.domain_code, 'favicon')}
                                  title={t('mail.domains.deleteFavicon', '刪除 favicon')}
                                  style={{ padding: '2px 6px', border: '1px solid #d32f2f', color: '#d32f2f', background: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>✕</button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        {canUpdate && (
                          <button onClick={() => startEdit(d)}
                            style={{ marginRight: 6, padding: '4px 10px', border: '1px solid #1976d2', color: '#1976d2', background: 'none', borderRadius: 4, cursor: 'pointer' }}>
                            {t('common.edit')}
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => handleDelete(d)}
                            style={{ padding: '4px 10px', border: '1px solid #d32f2f', color: '#d32f2f', background: 'none', borderRadius: 4, cursor: 'pointer' }}>
                            {t('common.delete')}
                          </button>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {domains.length === 0 && (
                <tr>
                  <td colSpan={10} style={{ ...tdStyle, textAlign: 'center', color: '#999' }}>
                    {t('common.noData')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default EEMailDomainsPage;
